import { Globe } from "lucide-react";
import type { ComponentType } from "react";

import {
  DiscordIcon,
  FacebookIcon,
  InstagramIcon,
  KickIcon,
  TikTokIcon,
  TwitchIcon,
  XIcon,
  YouTubeIcon,
} from "../icons/BrandIcons";
import { Input } from "../ui";
import type { ClubSettingsForm } from "./useClubSettingsForm";

type Props = {
  form: ClubSettingsForm;
};

type SocialField = {
  key: keyof ClubSettingsForm & `set${string}` extends never ? never : string;
  label: string;
  placeholder: string;
  icon: ComponentType<{ className?: string }>;
  color: string;
  value: string;
  setter: (value: string) => void;
  error?: string;
};

function getSocialFields(form: ClubSettingsForm): SocialField[] {
  return [
    {
      key: "discord",
      label: "Discord",
      placeholder: "discord.gg/...",
      icon: DiscordIcon,
      color: "text-discord",
      value: form.discordUrl,
      setter: (v) => form.setDiscordUrl(v),
      error: form.errors.discordUrl,
    },
    {
      key: "twitter",
      label: "Twitter / X",
      placeholder: "x.com/...",
      icon: XIcon,
      color: "text-white",
      value: form.twitterUrl,
      setter: (v) => form.setTwitterUrl(v),
      error: form.errors.twitterUrl,
    },
    {
      key: "website",
      label: "Website",
      placeholder: "https://...",
      icon: Globe,
      color: "text-brand-400",
      value: form.websiteUrl,
      setter: (v) => form.setWebsiteUrl(v),
      error: form.errors.websiteUrl,
    },
    {
      key: "youtube",
      label: "YouTube",
      placeholder: "youtube.com/@...",
      icon: YouTubeIcon,
      color: "text-youtube",
      value: form.youtubeUrl,
      setter: (v) => form.setYoutubeUrl(v),
      error: form.errors.youtubeUrl,
    },
    {
      key: "facebook",
      label: "Facebook",
      placeholder: "facebook.com/...",
      icon: FacebookIcon,
      color: "text-facebook",
      value: form.facebookUrl,
      setter: (v) => form.setFacebookUrl(v),
      error: form.errors.facebookUrl,
    },
    {
      key: "twitch",
      label: "Twitch",
      placeholder: "twitch.tv/...",
      icon: TwitchIcon,
      color: "text-twitch",
      value: form.twitchUrl,
      setter: (v) => form.setTwitchUrl(v),
      error: form.errors.twitchUrl,
    },
    {
      key: "instagram",
      label: "Instagram",
      placeholder: "instagram.com/...",
      icon: InstagramIcon,
      color: "text-instagram",
      value: form.instagramUrl,
      setter: (v) => form.setInstagramUrl(v),
      error: form.errors.instagramUrl,
    },
    {
      key: "tiktok",
      label: "TikTok",
      placeholder: "tiktok.com/@...",
      icon: TikTokIcon,
      color: "text-white",
      value: form.tiktokUrl,
      setter: (v) => form.setTiktokUrl(v),
      error: form.errors.tiktokUrl,
    },
    {
      key: "kick",
      label: "Kick",
      placeholder: "kick.com/...",
      icon: KickIcon,
      color: "text-kick",
      value: form.kickUrl,
      setter: (v) => form.setKickUrl(v),
      error: form.errors.kickUrl,
    },
  ];
}

export function SocialLinksSection({ form }: Props) {
  const fields = getSocialFields(form);

  return (
    <div className="space-y-3">
      <p className="text-xs text-surface-500">
        Add social links so members can find your community across platforms.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((field) => {
          const Icon = field.icon;
          return (
            <div
              key={field.key}
              className="rounded-lg border border-surface-700/40 bg-surface-800/40 px-3.5 py-3 space-y-2"
            >
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center size-7 rounded-md bg-surface-700/50 shrink-0">
                  <Icon className={`size-3.5 ${field.color}`} />
                </div>
                <span className="text-sm font-bold text-white">{field.label}</span>
              </div>
              <Input
                placeholder={field.placeholder}
                value={field.value}
                onChange={(e) => field.setter(e.target.value)}
                error={field.error}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
